import { useMemo, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { DataGrid, GridColDef, GridToolbar, GridRowSelectionModel } from '@mui/x-data-grid';
import type { MarketingMessage, MessageGenPhase } from '@trinity/chat';

export interface MessageGenerationTableProps {
  messages: MarketingMessage[];
  isGenerating: boolean;
  sessionId?: number;
  numberOfMessages?: number;
  phase?: MessageGenPhase;
  selectionModel: GridRowSelectionModel;
  onSelectionModelChange: (newSelection: GridRowSelectionModel) => void;
}

/**
 * Check if messages can be saved based on the current phase.
 * Messages can only be saved after curation is complete.
 */
function canSaveMessages(phase?: MessageGenPhase): boolean {
  return phase === 'curated';
}

export function MessageGenerationTable({
  messages,
  isGenerating,
  sessionId,
  numberOfMessages = 5,
  phase,
  selectionModel,
  onSelectionModelChange,
}: MessageGenerationTableProps): React.JSX.Element {
  const savingEnabled = canSaveMessages(phase);

  // Auto-select saved messages when messages load or change
  useEffect(() => {
    const savedIds = messages.filter((m) => m.saved).map((m) => m.identifier);
    if (savedIds.length > 0) {
      onSelectionModelChange((prev) => {
        const unsavedInSelection = prev.filter((id) => !savedIds.includes(id as string));
        return [...savedIds, ...unsavedInSelection];
      });
    }
  }, [messages, onSelectionModelChange]);
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        width: 200,
        flex: 1,
        renderCell: (params) => <TitleCell>{params.value}</TitleCell>,
      },
      {
        field: 'body',
        headerName: 'Message',
        width: 350,
        flex: 2,
        renderCell: (params) => <MessageCell>{params.value}</MessageCell>,
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 150,
        renderCell: (params) => <CategoryBadge>{params.value}</CategoryBadge>,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => (
          <StatusBadge status={params.value as 'draft' | 'approved' | 'published'}>
            {params.value}
          </StatusBadge>
        ),
      },
    ],
    []
  );

  const rows = useMemo(() => messages, [messages]);

  // Generate skeleton rows while loading
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: Math.min(numberOfMessages, 10) }).map((_, index) => ({
        identifier: `skeleton-${index}`,
        title: '',
        body: '',
        category: '',
        status: 'draft' as const,
      })),
    [numberOfMessages]
  );

  return (
    <Root>
      <DataGridContainer>
        <DataGrid
          rows={isGenerating ? skeletonRows : rows}
          columns={columns}
          getRowId={(row) => row.identifier}
          loading={isGenerating}
          checkboxSelection={savingEnabled}
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection) => {
            if (!savingEnabled) return;
            const savedIds = messages.filter((m) => m.saved).map((m) => m.identifier);
            const unsavedSelection = newSelection.filter((id) => {
              const msg = messages.find((m) => m.identifier === id);
              return msg && !msg.saved;
            });
            onSelectionModelChange([...savedIds, ...unsavedSelection]);
          }}
          isRowSelectable={(params) => savingEnabled && !params.row.saved}
          getRowClassName={(params) => (params.row.saved ? 'saved-row' : '')}
          density="compact"
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              csvOptions: { fileName: `messages-session-${sessionId}` },
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e0e0e0',
              fontSize: '10px',
              fontWeight: 600,
              color: '#1a1a1a',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              minHeight: '36px !important',
              maxHeight: '36px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '0 8px',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '10px',
              fontWeight: 600,
            },
            '& .MuiDataGrid-row': {
              '&.saved-row': {
                backgroundColor: 'rgba(16, 185, 129, 0.1) !important',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(18, 113, 196, 0.08) !important',
              },
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f9f9f9',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '12px',
              color: '#424242',
              borderBottom: '1px solid #e8e8e8',
              padding: '6px 8px',
            },
            '& .MuiCheckbox-root.Mui-disabled': {
              color: '#d1d5db',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '2px 6px',
              borderBottom: '1px solid #e0e0e0',
              gap: '2px',
              minHeight: '30px',
            },
            '& .MuiButton-root': {
              padding: '2px 6px',
              fontSize: '11px',
              minHeight: '24px',
            },
            '& .MuiTextField-root': {
              '& .MuiInputBase-root': {
                fontSize: '11px',
                padding: '0px 2px',
                minHeight: '24px',
              },
              '& .MuiInputBase-input': {
                padding: '2px 6px',
              },
            },
            '& .MuiDataGrid-overlay': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
          }}
        />
      </DataGridContainer>
    </Root>
  );
}

// Styled Components
const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const DataGridContainer = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  width: 100%;
`;

const TitleCell = styled.div`
  font-weight: 500;
  color: #1a1a1a;
`;

const MessageCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
`;

const CategoryBadge = styled.span`
  padding: 4px 10px;
  background: ${(props) => props.theme.palette.sentiment.primary.lighter};
  color: ${(props) => props.theme.palette.sentiment.primary.main};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: 'draft' | 'approved' | 'published' }>`
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#fef3c7';
      case 'approved':
        return '#d1fae5';
      case 'published':
        return '#dbeafe';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#92400e';
      case 'approved':
        return '#065f46';
      case 'published':
        return '#1e40af';
      default:
        return '#666';
    }
  }};
`;
